import L from "leaflet";
import { IconDefinition } from "@fortawesome/fontawesome-svg-core";
import markersSoft from "./images/markers-soft.png";
import markersShadow from "./images/markers-shadow.png";

interface AwesomeMarkerOptions extends Omit<L.IconOptions, "iconUrl"> {
    iconSize?: L.PointExpression;
    iconAnchor?: L.PointExpression;
    popupAnchor?: L.PointExpression;
    shadowAnchor?: L.PointExpression;
    shadowSize?: L.PointExpression;
    className?: string;
    prefix?: string;
    spinClass?: string;
    extraClasses?: string;
    icon?: string;
    markerColor?: "red" | "darkred" | "orange" | "green" | "darkgreen" | "blue" | "darkblue" | "purple" | "darkpurple" | "cadetblue" | "white" | "black";
    iconColor?: string;
    faIconDef?: IconDefinition;
    spin?: boolean;
}

class AwesomeIcon extends L.Icon {
    constructor(options: AwesomeMarkerOptions) {
        const defaultOptions: AwesomeMarkerOptions = {
            iconSize: [35, 45],
            iconAnchor: [17, 42],
            popupAnchor: [1, -32],
            shadowAnchor: [10, 12],
            shadowSize: [36, 16],
            className: "awesome-marker",
            prefix: "fa",
            spinClass: "fa-spin",
            extraClasses: "",
            icon: "home",
            markerColor: "blue",
            iconColor: "white",
        };

        super({ ...defaultOptions, ...options, iconUrl: "" });
    }

    createIcon(): HTMLElement {
        const div = document.createElement("div");
        const options = this.options as AwesomeMarkerOptions;

        div.className = `awesome-marker awesome-marker-icon-${options.markerColor}`;

        if (options.faIconDef) {
            div.innerHTML = this.createInner();
        }

        this.setIconStyles(div);
        return div;
    }

    private createInner(): string {
        const options = this.options as AwesomeMarkerOptions;

        if (options.faIconDef?.icon) {
            const [width, height, , , path] = options.faIconDef.icon;

            return `<svg 
                xmlns="http://www.w3.org/2000/svg" 
                viewBox="0 0 ${width} ${height}"
                class="${options.extraClasses} ${options.spin ? options.spinClass : ""}"
                style="${options.iconColor ? `color: ${options.iconColor}` : ""}"
            >
                <path fill="currentColor" d="${path}"></path>
            </svg>`;
        }

        return "";
    }

    private setIconStyles(div: HTMLElement): void {
        const options = this.options as AwesomeMarkerOptions;
        const size = L.point(options.iconSize as L.PointExpression);
        const anchor = L.point(options.iconAnchor as L.PointExpression);

        if (anchor) {
            div.style.marginLeft = `${-anchor.x}px`;
            div.style.marginTop = `${-anchor.y}px`;
        }

        if (size) {
            div.style.width = `${size.x}px`;
            div.style.height = `${size.y}px`;
        }
    }

    createShadow(): HTMLElement {
        const div = document.createElement("div");
        div.className = "awesome-marker-shadow";
        return div;
    }
}

export const createAwesomeIcon = (options: AwesomeMarkerOptions): L.Icon => {
    return new AwesomeIcon(options);
};

// CSS styles for the markers
const markerStyles = `
.awesome-marker {
    position: relative;
    width: 35px;
    height: 45px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: url(${markersSoft}) no-repeat 0 0;
    background-size: 720px 46px;
}

.awesome-marker-shadow {
    background: url(${markersShadow}) no-repeat 0 0;
    width: 40px;
    height: 42px;
    position: absolute;
    bottom: -3px;
    left: -12px;
}

.awesome-marker svg {
    width: 14px;
    height: 14px;
    position: relative;
    z-index: 1;
    margin-top: -6px;
    filter: drop-shadow(1px 1px 1px rgba(0, 0, 0, 0.4));
}

.awesome-marker i {
    color: #333;
    margin-top: -5px;
    font-size: 14px;
}

/* Colors */
.awesome-marker-icon-red { background-position: 0 0; }
.awesome-marker-icon-darkred { background-position: -180px 0; }
.awesome-marker-icon-lightred { background-position: -360px 0; }
.awesome-marker-icon-orange { background-position: -36px 0; }
.awesome-marker-icon-beige { background-position: -396px 0; }
.awesome-marker-icon-green { background-position: -72px 0; }
.awesome-marker-icon-darkgreen { background-position: -252px 0; }
.awesome-marker-icon-lightgreen { background-position: -432px 0; }
.awesome-marker-icon-blue { background-position: -108px 0; }
.awesome-marker-icon-darkblue { background-position: -216px 0; }
.awesome-marker-icon-lightblue { background-position: -468px 0; }
.awesome-marker-icon-purple { background-position: -144px 0; }
.awesome-marker-icon-darkpurple { background-position: -288px 0; }
.awesome-marker-icon-pink { background-position: -504px 0; }
.awesome-marker-icon-cadetblue { background-position: -324px 0; }
.awesome-marker-icon-white { background-position: -574px 0; }
.awesome-marker-icon-gray { background-position: -648px 0; }
.awesome-marker-icon-lightgray { background-position: -612px 0; }
.awesome-marker-icon-black { background-position: -682px 0; }

.awesome-marker-icon-white i { color: #333; }
.awesome-marker-icon-black i { color: #fff; }

.fa-spin {
    animation: fa-spin 2s infinite linear;
    transform-origin: center;
}

@keyframes fa-spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
}`;

// Inject styles into the document
const styleSheet = document.createElement("style");
styleSheet.textContent = markerStyles;
document.head.appendChild(styleSheet);
